import { DndContext, DragEndEvent, DragMoveEvent, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import React, { ReactNode, useCallback, useState } from 'react';
import styled from 'styled-components';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Icon } from '../../concepts/resources/Icon';
import { Point } from '../../platform/geom/Point';
import { Colors } from '../../ui';
import { useResolve } from '../Container';
import { boardMove } from './BoardCommands';

const DragItem = styled.div`
    display: flex;  
    flex-direction: row;
    align-items: center;
    gap: 1rem;
    width: 16rem;
    padding: 1rem;
    background: ${Colors.primaryLight};
    border-radius: .5rem;
    opacity: .8;
    color: white;
    font-size: 1.125rem;
    font-weight: 450;
`;

const BoardDragItem = ({ board }: { board: any }) => {
    return <DragItem>
        <Icon size="2" src="deqo://icons/dash/board" />
        {board?.label ?? 'Some Board'}
    </DragItem>;
};

const useOnMoveBoard = () => {

    const dispatch = useResolve(CommandDispatcher);

    return useCallback(async (boardId: string, folderId: string) => {
        await dispatch(boardMove, [boardId, folderId]);
    }, []);
};

export const WorkspaceDragContext = React.memo<{ children: ReactNode }>(({ children }) => {

    const [board, setBoard] = useState(null as any);
    const [dragStart, setDragStart] = useState(null as Point);

    const onBoardMove = useOnMoveBoard();

    const onDragMove = (e: DragMoveEvent) => {

        const at = Point.from(e.delta);
        if (!dragStart) {
            setDragStart(at);
        }
        else if (!board && e.active?.id?.startsWith('board')) {
            const dist = at.distanceTo(dragStart);
            if (dist > 10) {
                setBoard(e.active.data.current);
            }
        }
    };

    const onDragEnd = ({ active, over }: DragEndEvent) => {

        // Board into workspace (basically moving to root)
        if (active?.id?.startsWith('board') && over?.id?.startsWith('workspace')) {
            const boardId = active.id.split(':')[1];
            const workspaceId = over.id.split(':')[1];
            onBoardMove(boardId, workspaceId);
        }

        // Board into folder:
        if (active?.id?.startsWith('board') && over?.id?.startsWith('folder')) {
            const boardId = active.id.split(':')[1];
            const folderId = over.id.split(':')[1];
            onBoardMove(boardId, folderId);
        }

        setBoard(null);
        setDragStart(null);
    };

    const custom = useSensor(NoModifiersPointerSensor);
    const sensors = useSensors(custom);

    return <DndContext sensors={sensors}  {...{ onDragMove, onDragEnd }}>
        {children}
        <DragOverlay dropAnimation={null}>
            {!!board && <BoardDragItem board={board} />}
        </DragOverlay>
    </DndContext>;
});

export class NoModifiersPointerSensor extends PointerSensor {
    static activators = [
        {
            eventName: 'onPointerDown' as const,
            handler(e: any) {
                return !e.ctrlKey && !e.shiftKey;
            }
        }
    ];
}
